import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import FeedbackComment from "./FeedbackComment";

type Comment = {
  id: string;
  content: string;
  created_at: string;
  submitter_name: string | null;
};

type FeedbackCommentsProps = {
  feedbackId: string;
  initialComments?: Comment[];
};

const FeedbackComments = ({ feedbackId, initialComments = [] }: FeedbackCommentsProps) => {
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to comment",
          variant: "destructive",
        });
        return;
      }
      
      // First, get the user's profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', session.user.id)
        .single();
      
      // Then create the comment
      const { data: comment, error } = await supabase
        .from('feedback_comments')
        .insert({
          feedback_id: feedbackId,
          content: newComment.trim(),
          created_by: session.user.id,
        })
        .select()
        .single();

      if (error) throw error;

      setComments([...comments, {
        id: comment.id,
        content: comment.content,
        created_at: comment.created_at,
        submitter_name: profileData?.full_name || null,
      }]);
      setNewComment("");
      
      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add comment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <MessageCircle className="w-4 h-4" />
        <span className="text-sm font-medium">
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </span>
      </div>
      
      <div className="space-y-4">
        {comments.map((comment) => (
          <FeedbackComment
            key={comment.id}
            content={comment.content}
            createdAt={comment.created_at}
            submitterName={comment.submitter_name}
          />
        ))}
      </div>

      <div className="space-y-2">
        <Textarea
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <div className="flex justify-end">
          <Button 
            onClick={handleSubmitComment}
            disabled={isSubmitting || !newComment.trim()}
          >
            Add Comment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackComments;